import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { clearManagerMode } from "../lib/managerMode";
import { PLAN_CATALOG } from "../lib/planLimits";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const FUNCTIONS_BASE =
  (typeof window !== "undefined" && window.__FN_BASE__) ||
  process.env.REACT_APP_FUNCTIONS_BASE_URL ||
  "/api";

const B = {
  red:    "#980000",
  redDark:"#650202",
  orange: "#f15822",
  navy:   "#003353",
  steel:  "#657899",
  bg:     "#f6f7fb",
  white:  "#ffffff",
  border: "#e8ebf3",
  text:   "#1a1a2e",
};

function PageShell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: B.bg, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ background: B.white, borderBottom: `1px solid ${B.border}`, padding: "12px 20px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <a href="/"><img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 36, width: "auto" }} /></a>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <a href="/" style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.border}`, background: B.white, color: B.redDark, fontWeight: 700, fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}>← Home</a>
            <a href="/login" style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.redDark}`, background: B.redDark, color: B.white, fontWeight: 700, fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}>Log in</a>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "32px 20px 80px" }}>{children}</div>
    </div>
  );
}

export default function PersonalPlans() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isFreePlan, setIsFreePlan] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  useEffect(() => { clearManagerMode(); }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setIsFreePlan(false); return; }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const data = snap.exists() ? snap.data() : {};
        setIsFreePlan(data.subscriptionStatus === "free" || data.planKey === "free");
      } catch { setIsFreePlan(false); }
    });
    return () => unsub();
  }, []);

  async function selectPlan(plan) {
    if (plan.key === "family") {
      navigate("/family/signup");
      return;
    }
    clearManagerMode();

    // If already logged in (e.g. upgrading from free), go straight to Stripe checkout
    const currentUser = auth.currentUser;
    if (currentUser) {
      setCheckoutBusy(true);
      try {
        const idToken = await currentUser.getIdToken();
        const existingRefCode = (() => {
          try {
            const sp = new URLSearchParams(window.location.search || "");
            return (sp.get("ref") || sp.get("refCode") || "").trim().toUpperCase();
          } catch { return ""; }
        })();
        const r = await fetch(`${FUNCTIONS_BASE}/createCheckoutSession`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
          body: JSON.stringify({
            email: currentUser.email,
            priceId: plan.priceId,
            mode: "subscription",
            successUrl: window.location.origin + "/post-payment",
            cancelUrl: window.location.origin + "/personal/plans",
            metadata: {
              plan: plan.key,
              backendPlan: plan.backendKey || plan.key,
              profile_email: currentUser.email,
              profile_username: currentUser.email,
              accountType: "personal",
              uid: currentUser.uid,
              ...(existingRefCode ? { refCode: existingRefCode } : {}),
            },
          }),
        });
        const data = await r.json().catch(() => ({}));
        if (data?.url) {
          window.location.assign(data.url);
          return;
        }
        throw new Error(data?.error || "Could not start checkout. Please try again.");
      } catch (e) {
        alert(e.message || "Could not start checkout. Please try again.");
        setCheckoutBusy(false);
      }
      return;
    }

    // Not logged in - use the register flow as before
    try {
      localStorage.setItem("selectedPlan", plan.key);
      localStorage.setItem("selectedPlanBackendKey", plan.backendKey || plan.key);
      localStorage.setItem("selectedPriceId", plan.priceId);
      localStorage.setItem("selectedPrice", plan.priceId);
    } catch {}
    const planKey = encodeURIComponent(plan.key);
    const planBackendKey = encodeURIComponent(plan.backendKey || plan.key);
    const price = encodeURIComponent(plan.priceId);
    const keep = new URLSearchParams(location.search || "");
    const out = new URLSearchParams({ plan: planKey, backendPlan: planBackendKey, price, accountType: "personal" });
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
      const v = keep.get(k);
      if (v) out.set(k, v);
    });
    navigate(`/register?${out.toString()}`);
  }

  return (
    <PageShell>

      {/* Centred heading block */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ display: "inline-block", background: "#fff0ee", color: B.red, borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 700, marginBottom: 12, border: `1px solid ${B.orange}` }}>Personal plans</div>
        <p style={{ margin: "0 0 6px", color: B.red, fontSize: 16, fontWeight: 700 }}>Start free. No credit card required.</p>
        <h1 style={{ margin: "0 0 10px", color: B.redDark, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 800, lineHeight: 1.1 }}>Personal account plans</h1>
        <p style={{ maxWidth: 560, margin: "0 auto", color: B.steel, fontSize: 16, lineHeight: 1.6 }}>Start with a free account today - no credit card needed. Add your will, wishes, documents and messages. Upgrade when you're ready. Paid plans from &euro;49.99/year.</p>
        <div style={{ marginTop: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <a href="/register?plan=free" style={{ display: "block", background: B.red, color: "#fff", fontWeight: 800, fontSize: 16, padding: "14px 32px", borderRadius: 10, textDecoration: "none", boxShadow: "0 4px 14px rgba(152,0,0,0.20)", width: "100%", maxWidth: 360, textAlign: "center", boxSizing: "border-box" }}>
            Get started free &rarr;
          </a>
          <p style={{ margin: 0, fontSize: 13, color: B.steel }}>No credit card required.</p>
        </div>
      </div>

      {/* Two-column: trust bullets left, testimonial right */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 28, alignItems: "stretch" }}>
        {/* Trust bullets */}
        <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 12, padding: "20px 24px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 12 }}>
          <p style={{ margin: "0 0 4px", color: B.redDark, fontSize: 14, fontWeight: 800 }}>Why LastWishBox?</p>
          {[
            { icon: "🔒", text: "256-bit encrypted - your data is always secure" },
            { icon: "✓", text: "GDPR compliant - built for European privacy law" },
            { icon: "↩", text: "14-day money back guarantee - no questions asked" },
            { icon: "✕", text: "Cancel anytime - no lock-in, no hidden fees" },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: B.steel, fontWeight: 600 }}>
              <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </div>

        {/* Testimonial */}
        <div style={{ background: B.white, borderRadius: 12, borderLeft: `4px solid ${B.orange}`, padding: "20px 24px", boxShadow: "0 4px 16px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ display: "flex", gap: 2, marginBottom: 10 }}>
            {[1,2,3,4,5].map(s => <span key={s} style={{ color: B.orange, fontSize: 16 }}>★</span>)}
          </div>
          <p style={{ margin: "0 0 10px", color: B.text, fontSize: 15, fontStyle: "italic", lineHeight: 1.7 }}>"LastWishBox finally gave me one place to keep my will, insurance policies and important documents. My family know exactly where everything is now."</p>
          <p style={{ margin: 0, color: B.steel, fontSize: 13, fontWeight: 600 }}>- Mary O'Brien, Dublin</p>
        </div>
      </div>

      {/* Pricing cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 }}>
      {/* Upgrade nudge for existing free users */}
      {isFreePlan && (
        <div style={{ gridColumn: "1 / -1", background: "#fff0ee", border: `1px solid ${B.orange}`, borderRadius: 12, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 14, color: "#650202", fontWeight: 600 }}>You're on the free plan - choose a paid plan below to unlock unlimited access</span>
          <span style={{ fontSize: 13, color: B.steel }}>From &euro;49.99/year - less than &euro;1 a week</span>
        </div>
      )}

      {/* Free plan card - hidden for existing free users */}
      {!isFreePlan && (
      <div id="free-plan" style={{ background: "#f0f7ff", border: "1px solid #8cade1", borderRadius: 18, padding: 28, boxShadow: "0 4px 16px rgba(140,173,225,0.15)", display: "flex", flexDirection: "column", position: "relative" }}>
        <h2 style={{ margin: "0 0 4px", color: B.redDark, fontSize: 22, fontWeight: 800 }}>Free</h2>
        <p style={{ color: B.steel, fontSize: 13, margin: "0 0 10px", lineHeight: 1.5 }}>Perfect for getting started.</p>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "0 0 16px" }}>
          <span style={{ fontSize: 38, fontWeight: 900, color: B.text }}>&euro;0</span>
          <span style={{ fontSize: 14, color: B.steel, fontWeight: 600 }}>free forever</span>
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "flex", flexDirection: "column", gap: 8 }}>
          {["1 entry per section", "Document vault", "Message vault", "Funeral planning", "Email support"].map((f, i) => (
            <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, color: B.text }}>
              <span style={{ width: 20, height: 20, borderRadius: 6, background: "#fff0ee", border: `1px solid ${B.orange}`, display: "inline-flex", alignItems: "center", justifyContent: "center", color: B.red, fontWeight: 900, fontSize: 11, flexShrink: 0, marginTop: 1 }}>&#x2713;</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <a href="/register?plan=free" style={{ marginTop: "auto", padding: "13px 16px", borderRadius: 10, border: "none", background: "#003353", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", width: "100%", textAlign: "center", textDecoration: "none", boxSizing: "border-box", display: "block" }}>
          Get started free
        </a>
        <p style={{ margin: "8px 0 0", fontSize: 11, color: B.steel, textAlign: "center" }}>No credit card required</p>
      </div>
      )}

        {PLAN_CATALOG.filter(p => p.key !== "family").map((p) => (
          <div key={p.key} id={p.key} style={{ background: B.white, border: p.highlight ? `2px solid ${B.red}` : `1px solid ${B.border}`, borderRadius: 18, padding: 28, boxShadow: p.highlight ? "0 12px 36px rgba(152,0,0,0.12)" : "0 4px 16px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column", position: "relative" }}>
            {p.highlight && <span style={{ position: "absolute", top: 16, right: 16, background: B.red, color: B.white, borderRadius: 999, padding: "4px 10px", fontSize: 11, fontWeight: 800 }}>Most popular</span>}
            <h2 style={{ margin: "0 0 4px", color: B.redDark, fontSize: 22, fontWeight: 800 }}>{p.name}</h2>
            <p style={{ color: B.steel, fontSize: 13, margin: "0 0 10px", lineHeight: 1.5 }}>{p.blurb}</p>
            {p.key === "basic" && (
              <p style={{ margin: "0 0 4px", fontSize: 12, color: B.orange, fontWeight: 700 }}>Less than &euro;1 a week</p>
            )}
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "0 0 16px" }}>
              <span style={{ fontSize: 38, fontWeight: 900, color: B.text }}>{p.priceText}</span>
              <span style={{ fontSize: 14, color: B.steel, fontWeight: 600 }}>{p.cadence}</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "flex", flexDirection: "column", gap: 8 }}>
              {p.features.map((f, i) => (
                <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, color: B.text }}>
                  <span style={{ width: 20, height: 20, borderRadius: 6, background: "#fff0ee", border: `1px solid ${B.orange}`, display: "inline-flex", alignItems: "center", justifyContent: "center", color: B.red, fontWeight: 900, fontSize: 11, flexShrink: 0, marginTop: 1 }}>✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => selectPlan(p)} disabled={checkoutBusy} style={{ marginTop: "auto", padding: "13px 16px", borderRadius: 10, border: "none", background: checkoutBusy ? "#ccc" : B.red, color: B.white, fontWeight: 800, fontSize: 14, cursor: checkoutBusy ? "not-allowed" : "pointer", boxShadow: "0 4px 14px rgba(152,0,0,0.20)", width: "100%" }}>
              {checkoutBusy ? "Redirecting to checkout…" : `Get ${p.name}`}
            </button>
            <p style={{ margin: "8px 0 0", fontSize: 11, color: B.steel, textAlign: "center" }}>14-day money back guarantee · Cancel anytime · <a href="#free-plan" style={{ color: B.orange, textDecoration: "none", fontWeight: 700 }}>Or start free ↑</a></p>
          </div>
        ))}

        {/* Family plan card */}
        <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 18, padding: 28, boxShadow: "0 4px 16px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column", position: "relative" }}>
          <h2 style={{ margin: "0 0 4px", color: B.redDark, fontSize: 22, fontWeight: 800 }}>Family Plan</h2>
          <p style={{ color: B.steel, fontSize: 13, margin: "0 0 10px", lineHeight: 1.5 }}>One plan for the whole family.</p>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "0 0 16px" }}>
            <span style={{ fontSize: 38, fontWeight: 900, color: B.text }}>&euro;199.99</span>
            <span style={{ fontSize: 14, color: B.steel, fontWeight: 600 }}>/year</span>
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "flex", flexDirection: "column", gap: 8 }}>
            {["Up to 5 family members", "Each member gets their own private account", "3 account managers per member", "5 GB secure storage per member", "1 family organiser manages invites", "Priority support"].map((f, i) => (
              <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, color: B.text }}>
                <span style={{ width: 20, height: 20, borderRadius: 6, background: "#fff0ee", border: `1px solid ${B.orange}`, display: "inline-flex", alignItems: "center", justifyContent: "center", color: B.red, fontWeight: 900, fontSize: 11, flexShrink: 0, marginTop: 1 }}>&#x2713;</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => navigate("/family/signup")} style={{ marginTop: "auto", padding: "13px 16px", borderRadius: 10, border: "none", background: B.red, color: B.white, fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: "0 4px 14px rgba(152,0,0,0.20)", width: "100%" }}>
            Get Family Plan
          </button>
          <p style={{ margin: "8px 0 0", fontSize: 11, color: B.steel, textAlign: "center" }}>14-day money back guarantee - Cancel anytime</p>
        </div>
      </div>


      <p style={{ textAlign: "center", color: B.steel, fontSize: 14, marginTop: 28 }}>No hidden fees. Cancel anytime.</p>
    </PageShell>
  );
}
